/**
 * System Characteristic Service - React Query Integration
 * Handles all system characteristic-related API communications
 *
 * Important: System characteristics are global, admin-owned, and high-impact.
 * The list/detail endpoints also return user-owned characteristics for admin inspection.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import {
  SystemCharacteristic,
  CreateSystemCharacteristicInput,
  UpdateSystemCharacteristicInput,
} from '../types/admin';
import { AxiosError } from 'axios';
import { useNotification } from '../hooks/useNotification';

// ============================================
// API Response Types
// ============================================

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// ============================================
// Query Keys
// ============================================

export const systemCharacteristicKeys = {
  all: ['systemCharacteristics'] as const,
  lists: () => [...systemCharacteristicKeys.all, 'list'] as const,
  list: (filters: string) => [...systemCharacteristicKeys.lists(), { filters }] as const,
  details: () => [...systemCharacteristicKeys.all, 'detail'] as const,
  detail: (publicId: string) => [...systemCharacteristicKeys.details(), publicId] as const,
};

// ============================================
// API Functions
// ============================================

/**
 * Fetches all characteristics visible to admin.
 * Includes system characteristics and user-owned characteristics.
 */
const fetchSystemCharacteristics = async (): Promise<SystemCharacteristic[]> => {
  try {
    const response = await axiosInstance.get<ApiResponse<SystemCharacteristic[]>>(
      '/v1/admin/system-characteristics'
    );
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching system characteristics:', error);
    throw error;
  }
};

/**
 * Fetches a single characteristic by publicId for admin inspection.
 */
const fetchSystemCharacteristic = async (publicId: string): Promise<SystemCharacteristic> => {
  try {
    const response = await axiosInstance.get<ApiResponse<SystemCharacteristic>>(
      `/v1/admin/system-characteristics/${publicId}`
    );
    return response.data.data as SystemCharacteristic;
  } catch (error) {
    console.error('Error fetching system characteristic:', error);
    throw error;
  }
};

/**
 * Creates a new system characteristic
 * Note: isSystem is automatically set to true server-side
 */
const createSystemCharacteristic = async (
  input: CreateSystemCharacteristicInput
): Promise<SystemCharacteristic> => {
  try {
    const response = await axiosInstance.post<ApiResponse<SystemCharacteristic>>(
      '/v1/admin/system-characteristics',
      input
    );
    return response.data.data as SystemCharacteristic;
  } catch (error) {
    console.error('Error creating system characteristic:', error);
    throw error;
  }
};

/**
 * Updates an existing system characteristic
 * Note: code is immutable and cannot be updated
 * Note: isSystem cannot be changed by client
 */
const updateSystemCharacteristic = async ({
  publicId,
  ...input
}: UpdateSystemCharacteristicInput): Promise<SystemCharacteristic> => {
  try {
    const response = await axiosInstance.patch<ApiResponse<SystemCharacteristic>>(
      `/v1/admin/system-characteristics/${publicId}`,
      input
    );
    return response.data.data as SystemCharacteristic;
  } catch (error) {
    console.error('Error updating system characteristic:', error);
    throw error;
  }
};

// ============================================
// React Query Hooks
// ============================================

/**
 * Hook to fetch all system characteristics
 */
export const useSystemCharacteristics = () => {
  return useQuery({
    queryKey: systemCharacteristicKeys.lists(),
    queryFn: fetchSystemCharacteristics,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (previously cacheTime)
  });
};

/**
 * Hook to fetch a single system characteristic by publicId
 */
export const useSystemCharacteristic = (publicId: string) => {
  return useQuery({
    queryKey: systemCharacteristicKeys.detail(publicId),
    queryFn: () => fetchSystemCharacteristic(publicId),
    enabled: !!publicId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to create a new system characteristic
 * Displays success/error notifications
 */
export const useCreateSystemCharacteristic = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: createSystemCharacteristic,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: systemCharacteristicKeys.lists() });
      notification.success('System characteristic created successfully');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.message || 'Failed to create system characteristic';
      notification.error(message);
      console.error('Error creating system characteristic:', error);
    },
  });
};

/**
 * Hook to update an existing system characteristic
 * Displays success/error notifications
 */
export const useUpdateSystemCharacteristic = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: updateSystemCharacteristic,
    onSuccess: (data) => {
      // Invalidate both list and detail queries
      queryClient.invalidateQueries({ queryKey: systemCharacteristicKeys.lists() });
      queryClient.invalidateQueries({ queryKey: systemCharacteristicKeys.detail(data.publicId) });
      notification.success('System characteristic updated successfully');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.message || 'Failed to update system characteristic';
      notification.error(message);
      console.error('Error updating system characteristic:', error);
    },
  });
};
